import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
} from "@mui/material";
import { ReactElement, useState } from "react";
import { useForm } from "react-hook-form";

import { AddSubscriberSchema } from "@stw/yup-validators";

import {
  AudienceContactFieldType,
  CreateSubscriberMutation,
  CreateSubscriberMutationVariables,
  GetAudienceContactFieldsQuery,
  UploadSubscribersMutation,
  UploadSubscribersMutationVariables,
  UploadType,
} from "~frontend/__generated__/graphql";
import Loading from "~frontend/components/Loading";
import { useCsvUpload } from "~frontend/components/forms/CsvUpload/useCsvUpload";
import {
  RhfErrorBanner,
  RhfSelect,
  RhfSwitchField,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfYesNoSelect } from "~frontend/components/forms/react-hook-form-components/RhfYesNoSelect";
import {
  CREATE_SUBSCRIBER,
  GET_SUBACCOUNT_SHORTCODE,
  GET_SUBSCRIBER_SUMMARY,
  UPLOAD_SUBSCRIBERS,
} from "~frontend/graphql/shortcode";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { STATE_MENU_OPTIONS } from "~frontend/util/stateMenuOptions";
import useMutation from "~frontend/util/useMutation";
import { useStaffCsvFilenameValidator } from "~frontend/util/useStaffCsvFilenameValidator";

import { sortContactFields } from "./util";

export default function AddSubscriberDialog({
  onClose,
  shortcodeId,
  refreshList,
  openJobDetails,
  audienceContactFields,
}: {
  onClose: () => void;
  shortcodeId: string;
  refreshList: () => Promise<any>;
  openJobDetails: () => void;
  audienceContactFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
}): ReactElement {
  const [createSubscriber] = useMutation<
    CreateSubscriberMutation,
    CreateSubscriberMutationVariables
  >(CREATE_SUBSCRIBER, {
    refetchQueries: [
      {
        query: GET_SUBSCRIBER_SUMMARY,
        variables: { input: { shortcodeId } },
      },
      GET_SUBACCOUNT_SHORTCODE,
    ],
    awaitRefetchQueries: true,
  });

  const [uploadSubscribers] = useMutation<
    UploadSubscribersMutation,
    UploadSubscribersMutationVariables
  >(UPLOAD_SUBSCRIBERS);

  const getErrorMessage = useErrorMessageFormatter();
  const [overwritingCsv, setOverwritingCsv] = useState(false);
  const validator = useStaffCsvFilenameValidator();

  const allowDisableWelcomeMessage = useFeatureFlag(
    "addSubscribersWithoutSendingWelcomeMessage"
  );
  const customFieldsAllowed = useFeatureFlag("audienceCustomFields");

  const methods = useForm({
    resolver: zodResolver(AddSubscriberSchema(audienceContactFields)),
    mode: "onBlur",
    reValidateMode: "onBlur",
    values: {
      uploadMethod: "fileUpload",
      phoneNumberColumn: "",
      firstNameColumn: "",
      lastNameColumn: "",
      sendWelcomeMessage: true,
      audienceFieldColumns: audienceContactFields.reduce(
        (acc, field) => {
          if (customFieldsAllowed || !field.isEditable) {
            acc[field.name] = "";
          }
          return acc;
        },
        {} as Record<string, string>
      ),
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    formState: { isSubmitting },
  } = methods;

  const orderedContactFields = sortContactFields(
    audienceContactFields.filter(f => customFieldsAllowed || !f.isEditable)
  );

  const isManualEntry = watch("uploadMethod") === "textEntry";

  const { renderCsvUpload, startCsvUpload, csvState } = useCsvUpload({
    rhfControl: control,
    uploadType: UploadType.ContactCsv,
    overwriting: overwritingCsv,
    onChangeOverwriting: setOverwritingCsv,
    doNotRenderFileWhileImporting: true,
    renderProcessingState: () => {
      if (csvState === "IMPORTING_CSV" || csvState === "UPLOADED_CSV") {
        return <Loading />;
      }
      return null;
    },
    validCsvFilename: validator,
    columnPickerFields: orderedContactFields.map(field => ({
      name: `audienceFieldColumns.${field.name}`,
      label: field.displayName || field.name,
      autoMatchColumns: [field.name],
    })),
  });

  const isUploading = csvState === "UPLOADING_CSV";

  const onSubmit = async (rawValues: unknown) => {
    const values = AddSubscriberSchema(audienceContactFields).parse(rawValues);
    if (values.uploadMethod === "fileUpload") {
      const uploadMetadata = await startCsvUpload({
        phoneNumberColumn: values.phoneNumberColumn,
      });

      if (!uploadMetadata) {
        setError("root", {
          message:
            "Failed to start upload. Please try again or contact support if the error persists.",
        });
        return;
      }

      const { errors } = await uploadSubscribers({
        variables: {
          input: {
            shortcodeId: shortcodeId,
            estimatedRowCount: uploadMetadata.estimatedRowCount,
            phoneNumberColumn: values.phoneNumberColumn,
            silentBackfill: !values.sendWelcomeMessage,
            uploadToken: uploadMetadata.uploadToken,
            firstNameColumn:
              values.firstNameColumn ||
              values.audienceFieldColumns.first_name ||
              null,
            lastNameColumn:
              values.lastNameColumn ||
              values.audienceFieldColumns.last_name ||
              null,
            audienceFieldColumns: Object.keys(values.audienceFieldColumns).map(
              key => ({
                contactFieldName: key,
                csvColumnName: values.audienceFieldColumns[key],
              })
            ),
          },
        },
      });

      if (errors) {
        setError("root", { message: getErrorMessage(errors) });
      } else {
        reset();
        onClose();

        openJobDetails();
      }
    } else {
      // STW-2008 will tackle fixing the pattern of saving built-in field values alongside custom field values
      // in the same pattern as updating a subscriber
      const { errors } = await createSubscriber({
        variables: {
          input: {
            shortcodeId: shortcodeId,
            phoneNumber: values.phoneNumber,
            fields: JSON.stringify(values.fields),
            silentBackfill: !values.sendWelcomeMessage,
          },
        },
      });

      if (errors) {
        setError("root", { message: getErrorMessage(errors) });
      } else {
        await refreshList();
        reset();
        onClose();
      }
    }
  };

  return (
    <Dialog open maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle sx={{ pb: 0 }}>Add Subscribers</DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <RhfErrorBanner control={control} />
          <RhfSelect
            fullWidth
            label="Upload Method"
            name="uploadMethod"
            control={control}
          >
            <MenuItem value="fileUpload">File Upload</MenuItem>
            <MenuItem value="textEntry">Text Entry</MenuItem>
          </RhfSelect>

          {watch("uploadMethod") === "fileUpload" ? (
            <>
              {renderCsvUpload((field: string, name: string) =>
                setValue(field as any, name)
              )}
            </>
          ) : (
            <>
              <RhfTextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                control={control}
              />

              {orderedContactFields
                .filter(f => customFieldsAllowed || !f.isEditable)
                .map(field => {
                  const defaultParams = {
                    label: field.displayName || field.name,
                    name: `fields.${field.name}` as `fields.${string}`,
                    fullWidth: true,
                    control,
                  };
                  switch (field.type) {
                    case AudienceContactFieldType.String:
                      return (
                        <RhfTextField
                          {...defaultParams}
                          key={field.id}
                          characterLimit={field.typeParameters.maxLength || 255}
                        />
                      );
                    case AudienceContactFieldType.Boolean: {
                      return (
                        <RhfYesNoSelect {...defaultParams} key={field.id} />
                      );
                    }
                    case AudienceContactFieldType.Enum: {
                      return (
                        <RhfSelect
                          {...defaultParams}
                          key={field.id}
                          withNoneOption
                        >
                          {field.typeParameters.values?.map(option => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </RhfSelect>
                      );
                    }
                    case AudienceContactFieldType.Number: {
                      return <RhfTextField {...defaultParams} key={field.id} />;
                    }
                    case AudienceContactFieldType.Zip: {
                      return <RhfTextField {...defaultParams} key={field.id} />;
                    }
                    case AudienceContactFieldType.State: {
                      return (
                        <RhfSelect
                          {...defaultParams}
                          key={field.id}
                          withNoneOption
                        >
                          {STATE_MENU_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </RhfSelect>
                      );
                    }
                  }
                })}
            </>
          )}
          {allowDisableWelcomeMessage && (
            <RhfSwitchField
              label="Send opt in welcome message"
              name="sendWelcomeMessage"
              control={control}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={isSubmitting || isUploading} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || isUploading}
            color={"primary"}
            variant="contained"
          >
            {isSubmitting ? "Uploading..." : isManualEntry ? "Add" : "Upload"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
